import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import useAuth from '../pages/useAuth';
import AttendanceCard from '../components/AttendanceCards';
// rolewise dashboards
import HR_Dashboard from './rolewiseDashboards/HR_Dashboard';
import HRBP_Dashboard from './rolewiseDashboards/HRBP_Dashboard';
import RM_Dashboard from './rolewiseDashboards/RM_Dashboard';
import HOD_Doc_Dashboard from './rolewiseDashboards/HOD_Doc_Dashboard';
import SuperAdmin_Dashboard from './rolewiseDashboards/SuperAdmin_Dashboard';
import EMPLOYEE_Dashboard from './rolewiseDashboards/EMPLOYEE_Dashboard';
import USER_Dashboard from './rolewiseDashboards/USER_Dashboard';
import BM_Dashboard from './rolewiseDashboards/BM_Dashboard';
import HOD_CONTRACTUAL_Dashboard from './rolewiseDashboards/HOD_CONTRACTUAL_Dashboard';
import HOD_POOJARI_Dashboard from './rolewiseDashboards/HOD_POOJARI_Dashboard';
import HR_Radiology_Dashboard from './rolewiseDashboards/HR_Radiology_Dashboard';
import HR_Internal_Dashboard from './rolewiseDashboards/HR_Internal_Dashboard';
import UserRadiology_Dashboard from './rolewiseDashboards/UserRadiology_Dashboard';
import UserInternal_Dashboard from './rolewiseDashboards/UserInternal_Dashboard';
import Strategy_Dashboard from './rolewiseDashboards/Strategy_Dashboard';
import TeleSupport_Dashboard from './rolewiseDashboards/TeleSupport_Dashboard';


// 🔸 Status Summary Card Block
const CardBlock = ({ title, data, bg }) => (
     <div
          style={{
               backgroundColor: bg,
               borderRadius: '10px',
               padding: '16px 20px',
               minWidth: '220px',
               flex: '1 1 220px',
               boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}
     >
          <h5 style={{ fontSize: '16px', marginBottom: '12px', color: '#333' }}>{title}</h5>
          <div>
               {Object.entries(data || {}).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '6px', fontSize: '14px', color: '#555' }}>
                         <strong>{key}:</strong> {value}
                    </div>
               ))}
          </div>
     </div>
);

// 🔸 Main Dashboard Component
const Dashboard = () => {
     useAuth();

     // ✅ ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY CONDITIONAL RETURNS
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [attendanceData, setAttendanceData] = useState(null);
     const [loading, setLoading] = useState(true);
     const [statusCounts, setStatusCounts] = useState(null);

     // Get role info - check both 'role' and 'user_role' keys
     const user_role = (sessionStorage.getItem('user_role') || sessionStorage.getItem('role') || '').toUpperCase();
     const is_radiology_doctor = (sessionStorage.getItem('is_radiology_doctor') || 'N').toUpperCase();

     console.log('Dashboard - user_role:', user_role); // ✅ Debug log

     // Determine which dashboard to show
     const roleDashboardRoles = [
          'HOD_DOCTORS',
          'HOD_CONTRACTUAL',
          'HOD_POOJARI',
          'BRANCH_MANAGER',
          'EMPLOYEE',
          'REPORTING_MANAGER',
          'HR_RADIOLOGY',
          'HR_INTERNAL',
          'HRBP',
          'HR',
          'USER_RADIOLOGY',
          'USER_INTERNAL',
          'STRATEGY',
          'SUPER_ADMIN',
          'TELE_SUPPORT',
          // Backward-compatible aliases still used in session values.
          'RM',
          'BM',
          'USER'
     ];
     const shouldShowRoleDashboard = roleDashboardRoles.includes(user_role);

     // Only fetch attendance if NOT showing role dashboard
     useEffect(() => {
          if (shouldShowRoleDashboard) return;

          const fetchAttendance = async () => {
               try {
                    setLoading(true);
                    const response = await fetch(`${baseUrl}/getUserAttendance`, {
                         method: "GET",
                         headers: {
                              "Content-Type": "application/json",
                              "Authorization": `Bearer ${sessionStorage.getItem("token")}`
                         },
                    });
                    const data = await response.json();
                    if (data.status) setAttendanceData(data.data);
               } catch (error) {
                    console.error("Error fetching attendance:", error);
               } finally {
                    setLoading(false);
               }
          };
          fetchAttendance();
     }, [baseUrl, shouldShowRoleDashboard]);

     // Only fetch status counts if NOT showing role dashboard
     useEffect(() => {
          if (shouldShowRoleDashboard) return;

          const fetchStatusCounts = async () => {
               try {
                    const response = await fetch(`${baseUrl}/getStatus`, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`
                         }
                    });
                    const result = await response.json();
                    if (result.status) setStatusCounts(result.data);
               } catch (error) {
                    console.error("Error fetching status counts:", error);
               }
          };
          fetchStatusCounts();
     }, [baseUrl, shouldShowRoleDashboard]);

     // ✅ NOW SAFE TO DO CONDITIONAL RETURNS (after all hooks)
     if (user_role === 'SUPER_ADMIN') return <SuperAdmin_Dashboard />;
     else if (user_role === 'HR') return <HR_Dashboard />;
     else if (user_role === 'HRBP') return <HRBP_Dashboard />;
     else if (user_role === 'STRATEGY') return <Strategy_Dashboard />;
     else if (user_role === 'REPORTING_MANAGER' || user_role === 'RM') return <RM_Dashboard />;
     else if (user_role === 'HOD_DOCTORS') return <HOD_Doc_Dashboard />;
     else if (user_role === 'BRANCH_MANAGER' || user_role === 'BM') return <BM_Dashboard />;
     else if (user_role === 'EMPLOYEE') return <EMPLOYEE_Dashboard />;
     else if (user_role === 'USER') return <USER_Dashboard />;
     else if (user_role === 'HOD_CONTRACTUAL') return <HOD_Doc_Dashboard />;
     else if (user_role === 'HOD_POOJARI') return <HOD_POOJARI_Dashboard />;
     else if (user_role === 'HR_RADIOLOGY') return <HR_Radiology_Dashboard />;
     else if (user_role === 'HR_INTERNAL') return <HR_Internal_Dashboard />;
     else if (user_role === 'USER_RADIOLOGY') return <UserRadiology_Dashboard />;
     else if (user_role === 'USER_INTERNAL') return <UserInternal_Dashboard />;
     else if (user_role === 'TELE_SUPPORT') return <TeleSupport_Dashboard />;


     // Default rendering for radiology doctors or other users
     const todayPunchIn = attendanceData?.today?.punch_in || "-";
     const todayPunchOut = attendanceData?.today?.punch_out || "-";

     return (
          <div style={{ padding: '24px', backgroundColor: '#f7f9fc', minHeight: '100vh' }}>
               <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {statusCounts && (
                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                              <CardBlock title="Manager Status - My" data={statusCounts.rpt_mgr_status_counts_user} bg="#e8f0fe" />
                              <CardBlock title="HR Status - My" data={statusCounts.hr_status_counts_user} bg="#fdecea" />
                         </div>
                    )}
                    <Row style={{ marginBottom: '24px' }}>
                         <Col md={9}>
                              <AttendanceCard title="Today's Attendance" present={todayPunchIn} absent={todayPunchOut} />
                         </Col>
                    </Row>
               </div>
          </div>
     );
};

export default Dashboard;